import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Check,
  AlertTriangle,
  Clock,
  Smartphone,
  RefreshCw,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthService from '../../services/authService';
import AuthShell, { AuthNotice } from './AuthShell';

const MFARecoveryVerify = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState('');
  const [selectedAction, setSelectedAction] = useState('remove_trusted_devices');

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);


  const verifyToken = async () => {
    try {
      setLoading(true);
      const data = await AuthService.verifyMFARecoveryToken(token);
      setTokenData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired recovery link');
    } finally {
      setLoading(false);
    }
  };

  const executeRecovery = async () => {
    try {
      setSubmitting(true);
      const response = await AuthService.executeMFARecovery(token, selectedAction);
      
      toast.success('Account recovery completed successfully!');
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: response.detail || 'Account recovery completed successfully!',
            type: 'success'
          }
        });
      }, 2000);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recovery failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const recoveryOptions = [
    {
      id: 'remove_trusted_devices',
      title: 'Remove Trusted Devices',
      subtitle: 'Recommended',
      description: 'Remove all trusted devices. You\'ll need to verify MFA again the next time you log in from any device.',
      icon: Smartphone,
      recommended: true
    },
    {
      id: 'reset_mfa',
      title: 'Reset MFA Completely',
      subtitle: 'Complete reset',
      description: 'Remove all MFA methods and disable MFA entirely. You can set up new MFA methods later.',
      icon: RefreshCw,
      recommended: false
    },
  ];

  if (loading) {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-3 py-10 text-sm text-gray-500 dark:text-gray-400" role="status">
          <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
          Verifying recovery link...
        </div>
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Invalid Recovery Link"
        subtitle={error}
      >
        <button
          onClick={() => navigate('/mfa/recovery/request')}
          className="btn btn-primary btn-lg w-full"
        >
          Request New Recovery Link
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      maxWidth="max-w-xl"
      icon={<ShieldCheck className="h-6 w-6" />}
      title="MFA Account Recovery"
      subtitle="Choose how you'd like to recover access to your account"
    >
      <div className="space-y-6">
        {/* Account Info */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-700 dark:bg-gray-800">
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">Account: {tokenData?.user_email || 'Loading...'}</p>
            <p className="text-gray-500 dark:text-gray-400">Recovery Link: Valid</p>
          </div>
          <p className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
            <Clock className="h-4 w-4" />
            Expires: {tokenData?.expires_at ? new Date(tokenData.expires_at).toLocaleString() : 'Unknown'}
          </p>
        </div>

        {/* Current MFA Status */}
        {tokenData?.current_mfa_methods?.length > 0 && (
          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Current MFA Methods</h3>
            <div className="space-y-2">
              {tokenData.current_mfa_methods.map((method, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 dark:text-gray-300">{method.device_name || method.type}</span>
                  {method.is_primary && <span className="badge badge-brand">Primary</span>}
                </div>
              ))}
              {tokenData.trusted_devices_count > 0 && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {tokenData.trusted_devices_count} trusted device(s)
                </p>
              )}
            </div>
          </div>
        )}

        {/* Recovery Options */}
        <fieldset className="space-y-3">
          <legend className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Choose Recovery Action</legend>
          {recoveryOptions.map((option) => {
            const IconComponent = option.icon;
            const selected = selectedAction === option.id;
            return (
              <button
                type="button"
                key={option.id}
                aria-pressed={selected}
                className={`relative flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                  selected
                    ? 'border-primary-500 bg-primary-50/60 ring-1 ring-primary-500 dark:bg-primary-400/10'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
                onClick={() => setSelectedAction(option.id)}
              >
                <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                  selected ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  <IconComponent className="h-4 w-4" />
                </span>
                <span className="flex-1">
                  <span className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{option.title}</span>
                    <span className={`badge ${option.recommended ? 'badge-success' : 'badge-gray'}`}>{option.subtitle}</span>
                  </span>
                  <span className="block text-sm text-gray-500 dark:text-gray-400">{option.description}</span>
                </span>
                <span className={`mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                  selected ? 'border-primary-600 bg-primary-600' : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </span>
              </button>
            );
          })}
        </fieldset>

        {/* Security Notice */}
        <AuthNotice tone="warning" role="note">
          <span className="font-medium">Security Notice:</span> After recovery, if MFA is required for your account, you'll need to set up MFA again to maintain account security.
          This action cannot be undone.
        </AuthNotice>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button onClick={() => navigate('/login')} className="btn btn-secondary btn-lg">
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </button>
          <button onClick={executeRecovery} disabled={submitting} className="btn btn-primary btn-lg flex-1">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Complete Recovery'
            )}
          </button>
        </div>
      </div>
    </AuthShell>
  );
};

export default MFARecoveryVerify;
